import React, { createContext, useContext, useState, useEffect } from 'react';
import { LanguageLoader } from './stringsFiles/languageLoader';
import type { Language, AppStrings } from '../../types/strings';

// Re-export types for convenience
export type { Language, AppStrings } from '../../types/strings';

// Language context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  strings: AppStrings;
  t: (category: keyof AppStrings, key: string) => string;
}

// Create context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Language provider component
interface LanguageProviderProps {
  children: React.ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>('en');
  const [strings, setStrings] = useState<AppStrings | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load strings for current language
  useEffect(() => {
    const loadStrings = async () => {
      console.log('[LanguageProvider] Loading strings for language:', language);
      
      // Check if language is already cached for instant switching
      const cachedStrings = LanguageLoader.getCached(language);
      if (cachedStrings) {
        console.log('[LanguageProvider] Using cached strings for:', language);
        setStrings(cachedStrings);
        setIsInitialLoad(false);
        return;
      }
      
      // Load the language (with fallback handling built-in)
      const loadedStrings = await LanguageLoader.loadLanguage(language);
      setStrings(loadedStrings);
      setIsInitialLoad(false);
    };

    loadStrings();
  }, [language]);

  // Pre-load all languages in the background after initial load
  useEffect(() => {
    if (!isInitialLoad && strings) {
      // Pre-load with a small delay to not interfere with initial render
      setTimeout(() => {
        LanguageLoader.preloadAllLanguages();
      }, 100);
    }
  }, [isInitialLoad, strings]);

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('astroshifts-language', language);
  }, [language]);

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('astroshifts-language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'vn')) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Translation function
  const t = (category: keyof AppStrings, key: string): string => {
    if (!strings) return key;
    
    const categoryStrings = strings[category] as any;
    if (!categoryStrings || !categoryStrings[key]) {
      console.warn(`Missing translation: ${category}.${key} for language ${language}`);
      return key;
    }
    
    return categoryStrings[key];
  };

  // Only show loading screen on initial app load, not during language switches
  if (isInitialLoad || !strings) {
    console.log('[LanguageProvider] Showing loading screen');
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #1e293b, #334155)',
        color: 'white',
        fontSize: '1.25rem'
      }}>
        Loading language files...
      </div>
    );
  }

  console.log('[LanguageProvider] Rendering app with strings loaded for language:', language);

  const value: LanguageContextType = {
    language,
    setLanguage,
    strings,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to use language context
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Convenience hook for translation function
export function useTranslation() {
  const { t } = useLanguage();
  return { t };
}